import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeftIcon, ArrowRightIcon } from '@App/common/components/icons/NavigationArrows';
import * as styles from './Products.module.scss';
const renderStarIcon = () => /*#__PURE__*/React.createElement("svg", {
  className: styles.rootCardStarIcon,
  width: "13",
  height: "13",
  viewBox: "0 0 13 13",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  // eslint-disable-next-line max-len
  d: "M5.814 1.05c.154-.312.231-.468.336-.518a.334.334 0 0 1 .287 0c.105.05.181.206.335.518l1.46 2.958a.73.73 0 0 0 .102.174c.03.031.064.057.104.075.044.02.095.028.196.043l3.266.477c.344.05.516.076.595.16.07.073.102.173.089.273-.015.115-.14.236-.389.478L9.833 7.99c-.074.072-.11.108-.134.15a.334.334 0 0 0-.04.123c-.006.048.003.099.02.2l.558 3.25c.058.343.088.514.033.616a.334.334 0 0 1-.233.169c-.114.02-.268-.06-.575-.222l-2.92-1.535a.73.73 0 0 0-.184-.082.33.33 0 0 0-.129 0c-.048.01-.093.034-.184.082l-2.92 1.535c-.308.162-.462.243-.575.222a.334.334 0 0 1-.233-.17c-.055-.1-.026-.272.033-.615l.558-3.25a.73.73 0 0 0 .02-.2.334.334 0 0 0-.04-.123c-.024-.042-.06-.078-.134-.15L.39 5.69c-.248-.243-.373-.364-.388-.48-.013-.099.02-.2.088-.272.08-.084.252-.11.596-.16L3.953 4.3c.101-.015.152-.022.196-.043a.334.334 0 0 0 .104-.075c.033-.036.056-.082.101-.174l1.46-2.958Z"
}));
const parseRatingValue = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
const formatRatingValue = value => {
  const parsed = parseRatingValue(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed.toFixed(1);
};
const renderStars = rating => {
  const parsed = parseRatingValue(rating);
  const safeRating = Number.isFinite(parsed) ? Math.max(0, Math.min(5, Math.round(parsed))) : 0;
  return Array.from({
    length: 5
  }, (_, index) => /*#__PURE__*/React.createElement("span", {
    key: index,
    className: `${styles.rootCardStar} ${index < safeRating ? styles.rootCardStarFilled : ''}`,
    "aria-hidden": "true"
  }, renderStarIcon()));
};
const renderProductImage = image => {
  if (!image) {
    return null;
  }
  const desktop = image.desktop || {};
  const mobile = image.mobile || {};
  const fallbackSrc = desktop.png || mobile.png;
  if (!fallbackSrc) {
    return null;
  }
  return /*#__PURE__*/React.createElement("picture", null, mobile.avif && /*#__PURE__*/React.createElement("source", {
    srcSet: mobile.avif,
    type: "image/avif"
  }), mobile.webp && /*#__PURE__*/React.createElement("source", {
    srcSet: mobile.webp,
    type: "image/webp"
  }), mobile.png && /*#__PURE__*/React.createElement("source", {
    srcSet: mobile.png
  }), desktop.avif && /*#__PURE__*/React.createElement("source", {
    srcSet: desktop.avif,
    type: "image/avif",
    media: "(min-width: 810px)"
  }), desktop.webp && /*#__PURE__*/React.createElement("source", {
    srcSet: desktop.webp,
    type: "image/webp",
    media: "(min-width: 810px)"
  }), mobile.png && /*#__PURE__*/React.createElement("source", {
    srcSet: desktop.png,
    media: "(min-width: 810px)"
  }), /*#__PURE__*/React.createElement("img", {
    className: styles.rootCardImage,
    src: fallbackSrc,
    alt: image.alt || '',
    loading: "lazy"
  }));
};
const Products = ({
  heading,
  tabs = [],
  sliderSettings = {}
}) => {
  const {
    0: activeIndex,
    1: setActiveIndex
  } = useState(0);
  const emblaOptions = useMemo(() => {
    const currentSettings = sliderSettings || {};
    const {
      gap,
      autoplay,
      hoverpause,
      ...rest
    } = currentSettings;
    return {
      align: 'start',
      containScroll: 'trimSnaps',
      skipSnaps: false,
      loop: false,
      ...rest
    };
  }, [sliderSettings]);
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const {
    0: selectedIndex,
    1: setSelectedIndex
  } = useState(0);
  const {
    0: scrollSnaps,
    1: setScrollSnaps
  } = useState([]);
  const {
    0: canScrollPrev,
    1: setCanScrollPrev
  } = useState(false);
  const {
    0: canScrollNext,
    1: setCanScrollNext
  } = useState(false);
  const handleTabClick = useCallback(index => {
    setActiveIndex(index);
  }, []);
  const updateNavigationState = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  const refreshScrollSnaps = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);
  if (!(tabs !== null && tabs !== void 0 && tabs.length)) {
    return null;
  }
  const safeIndex = Math.min(activeIndex, tabs.length - 1);
  const activeTab = tabs[safeIndex];
  const items = (activeTab === null || activeTab === void 0 ? void 0 : activeTab.items) || [];
  const activeHref = activeTab === null || activeTab === void 0 ? void 0 : activeTab.href;
  const normalizedItems = useMemo(() => items || [], [items]);
  const sliderKey = `${safeIndex}-${normalizedItems.map(item => {
    var _item$id;
    return (_item$id = item === null || item === void 0 ? void 0 : item.id) !== null && _item$id !== void 0 ? _item$id : '';
  }).join('|')}`;
  const carouselGap = useMemo(() => {
    const value = sliderSettings === null || sliderSettings === void 0 ? void 0 : sliderSettings.gap;
    return typeof value === 'number' ? value : 32;
  }, [sliderSettings]);
  const handlePrev = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
  }, [emblaApi]);
  const handleNext = useCallback(() => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
  }, [emblaApi]);
  const handleDotClick = useCallback(index => {
    emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index);
  }, [emblaApi]);
  const shouldRenderArrows = scrollSnaps.length > 1;
  const shouldRenderDots = scrollSnaps.length > 1;
  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }
    const syncState = () => {
      refreshScrollSnaps();
      updateNavigationState();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    syncState();
    emblaApi.on('select', syncState);
    emblaApi.on('reInit', syncState);
    emblaApi.on('resize', syncState);
    return () => {
      emblaApi.off('select', syncState);
      emblaApi.off('reInit', syncState);
      emblaApi.off('resize', syncState);
    };
  }, [emblaApi, updateNavigationState]);
  return /*#__PURE__*/React.createElement("section", {
    className: styles.root
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootContainer
  }, heading && /*#__PURE__*/React.createElement("h2", {
    className: styles.rootHeading,
    dangerouslySetInnerHTML: {
      __html: heading
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.rootTabs,
    role: "tablist",
    "aria-label": heading || 'Products'
  }, tabs.map(({
    label,
    href
  }, index) => {
    const isActive = index === safeIndex;
    return href ? /*#__PURE__*/React.createElement("a", {
      key: label,
      className: `${styles.rootTab} ${styles.rootTabLink}`,
      href: href
    }, label) : /*#__PURE__*/React.createElement("button", {
      key: label,
      type: "button",
      role: "tab",
      "aria-selected": isActive,
      className: `${styles.rootTab} ${isActive ? styles.rootTabActive : ''}`,
      onClick: () => handleTabClick(index)
    }, label);
  })), normalizedItems.length > 0 ? /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarousel,
    key: sliderKey,
    style: {
      '--products-carousel-gap': `${carouselGap}px`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarouselViewport,
    ref: emblaRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootCarouselContainer
  }, normalizedItems.map(({
    id,
    title,
    category,
    price,
    image,
    rating,
    link
  }, index) => {
    const slideKey = id ? `${id}-${index}` : `product-${index}`;
    const parsedRating = parseRatingValue(rating);
    return /*#__PURE__*/React.createElement("div", {
      key: slideKey,
      className: styles.rootSlide
    }, /*#__PURE__*/React.createElement("article", {
      className: styles.rootCard,
      "aria-hidden": false,
      tabIndex: undefined
    }, /*#__PURE__*/React.createElement("figure", {
      className: styles.rootCardFigure
    }, renderProductImage(image)), /*#__PURE__*/React.createElement("div", {
      className: styles.rootCardRating
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.rootCardStars,
      "aria-hidden": "true"
    }, renderStars(parsedRating)), Number.isFinite(parsedRating) && /*#__PURE__*/React.createElement("span", {
      className: styles.rootCardRatingValue
    }, formatRatingValue(parsedRating))), /*#__PURE__*/React.createElement("div", {
      className: styles.rootCardHead
    }, /*#__PURE__*/React.createElement("h3", {
      className: styles.rootCardHeadText
    }, link ? /*#__PURE__*/React.createElement("a", {
      className: styles.rootCardLink,
      href: link,
      dangerouslySetInnerHTML: {
        __html: title
      }
    }) : /*#__PURE__*/React.createElement("span", {
      dangerouslySetInnerHTML: {
        __html: title
      }
    })), /*#__PURE__*/React.createElement("h4", {
      className: styles.rootCardHeadText
    }, category)), /*#__PURE__*/React.createElement("p", {
      className: styles.rootCardPrice
    }, price)));
  }))), (shouldRenderDots || shouldRenderArrows) && /*#__PURE__*/React.createElement("div", {
    className: styles.rootPagination
  }, shouldRenderDots && /*#__PURE__*/React.createElement("div", {
    className: styles.rootDots
  }, scrollSnaps.map((_, index) => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: `products-dot-${index}`,
    className: `${styles.rootDot} ${index === selectedIndex ? styles.rootDotActive : ''}`,
    onClick: () => handleDotClick(index),
    "aria-label": `Przejdź do produktu ${index + 1}`,
    "aria-current": index === selectedIndex ? 'true' : undefined
  }))), shouldRenderArrows && /*#__PURE__*/React.createElement("div", {
    className: styles.rootArrows
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `${styles.rootArrow} ${styles.rootArrowPrev}`,
    onClick: handlePrev,
    disabled: !canScrollPrev,
    "aria-label": "Poprzedni produkt"
  }, /*#__PURE__*/React.createElement(ArrowLeftIcon, null)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `${styles.rootArrow} ${styles.rootArrowNext}`,
    onClick: handleNext,
    disabled: !canScrollNext,
    "aria-label": "Nast\u0119pny produkt"
  }, /*#__PURE__*/React.createElement(ArrowRightIcon, null))))) : activeHref ? /*#__PURE__*/React.createElement("div", {
    className: styles.rootEmptyState
  }, /*#__PURE__*/React.createElement("a", {
    className: styles.rootEmptyLink,
    href: activeHref
  }, activeTab.label)) : null));
};
export default Products;